import * as React from "react";
import {Legend, LegendItem} from "../../../components/charts/Legend";
import {ColorUtils} from "../../../components/Utils/color/ColorUtils";
import {Loader} from "../../../components/Utils/loader/Loader";
import {StatisticsUtils} from "../../../components/Utils/statistics/StatisticsUtils";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {CategoriesColors, EquipmentPlacementTexts, Placements, WidgetResponse} from "../../DashboardContainer";
import {JSONMapper} from "../../data/JSONMapper";
import {StackedBarChart, StackedBarChartRow} from "../charts/StackedBarChart";
import {REGISTERED_LOCATIONS_IDS, SiteTypes} from "./MarketWidget";
import {RSL_HIERARCHY_GUID} from "./OpenAlarmsByLevelWidget";
import {RestDataLoader} from "./RestDataLoader";
import "./style/EquipmentByLevelWidget.scss";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {WidgetBadge, WidgetBadgeType} from "../DashboardWidget";
import AutoSizer from "react-virtualized-auto-sizer";

declare var AppTranslations: AppTranslations;

interface EquipmentByRegionWidgetProps {
    data: WidgetResponse;
    texts: EquipmentPlacementTexts;
    withTelemetryDevice: boolean;
    colorGenerator: ColorUtils;
}

interface WidgetState {
    ready: boolean;
    error: boolean;
    equipmentByLevel: EquipmentByCommercialNode[];
    selectedItemKey: string;
}

export interface EquipmentByPlacement {
    placementGuid: string;
    placementName: string;
    equipmentCount: number;
}

export interface EquipmentByCommercialNode {
    levelName: string;
    levelKey?: string;
    equipmentByPlacement: EquipmentByPlacement[];
}

export const ZERO_GUID = "00000000-0000-0000-0000-000000000000";

export const getSiteType = (guid: string): SiteTypes[] => {
    switch (guid) {
        case Placements.IN_UNKNOWN_LOCATION:
            return [SiteTypes.UNREGISTERED_SITE];
        case Placements.ON_MARKET:
            return [...REGISTERED_LOCATIONS_IDS, SiteTypes.UNREGISTERED_SITE];
        case Placements.IN_WAREHOUSE:
            return [SiteTypes.WAREHOUSE];
        case Placements.AT_MANUFACTURERS:
            return [SiteTypes.FACTORY];
        default:
            return [];
    }
};

export class EquipmentByLevelWidget extends React.Component<EquipmentByRegionWidgetProps, WidgetState> {
    constructor(props: EquipmentByRegionWidgetProps) {
        super(props);
        this.state = {
            ready: false,
            error: false,
            equipmentByLevel: null,
            selectedItemKey: null,
        };
        this.setSelectedItem = this.setSelectedItem.bind(this);
    }

    public setSelectedItem(itemKey: string): void {
        this.setState({selectedItemKey: itemKey});
    }

    public ready() {
        if (this.state.equipmentByLevel != null) {
            this.setState({ready: true});
        }
    }

    public componentDidMount() {
        RestDataLoader.loadData(
            "statistics/equipment/counting/commercialHierarchyAndPlacement",
            {
                placements: "warehouse,market",
                withTelemetry: this.props.withTelemetryDevice,
            },
            (data: any) => {
                this.setState({
                    equipmentByLevel: JSONMapper.jsonEquipmentCountByCommercialHierarchyMapper(data.list),
                });
                this.ready();
            },
            () => {
                this.setState({error: true});
            },
        );
    }

    public render() {
        if (this.state.error) {
            return null;
        }
        let content = <Loader/>;
        if (!this.state.ready) {
            return (
                <div className="equipment-by-region-widget">
                    {content}
                </div>
            );
        }
        let data: StackedBarChartRow[] = this.state.equipmentByLevel.map(
            (item, i) => {
                return {
                    label: item.levelName,
                    subItems: item.equipmentByPlacement.map(
                        (sitem, j) => {
                            return {
                                label: this.getText(sitem.placementGuid),
                                code: sitem.placementGuid,
                                value: sitem.equipmentCount,
                            };
                        },
                    ),
                };
            },
        );

        let legendItems: LegendItem[] = [];
        let total = 0;
        // retrieve legend items from data
        data.forEach((d) => {
            d.subItems.forEach((si) => {
                const legendItem = legendItems.filter((li) => li.code === si.code);
                if (legendItem.length === 0) {
                    legendItems.push({
                        name: si.label,
                        code: si.code,
                        color: null,
                        value: si.value,
                    });
                } else {
                    legendItem[0].value += si.value;
                }
                total += si.value;
            });
        });

        // define color for each legend item
        legendItems = legendItems.map((li, i) => {
            return {
                code: li.code,
                name: li.name,
                value: li.value,
                percentage: StatisticsUtils.getPercentage(total, li.value),
                color: this.props.colorGenerator.getMappedColor(CategoriesColors.PLACEMENT, li.code),
            };
        });

        data = StackedBarChart.reduceBars(data, 10, this.props.texts);

        content = (
            <VeBoxCard className="equipment-by-region-widget__container home-dashboard-card">
                <VeBoxCard.Header
                    className="equipment-by-region-widget__header commercial-header space-between"
                    title={this.props.texts.title}
                    titleClassName={"lowercase capitalize"}
                    singleLineTitle={true}
                    back={
                        <WidgetBadge type={WidgetBadgeType.COMMERCIAL_W}/>
                    }
                />
                <VeBoxCard.Body className="equipment-by-region-widget__content">
                    <div className="stacked-bar-chart__legend__container">
                        <Legend
                            items={legendItems}
                            selectedItemName={this.state.selectedItemKey}
                            onItemHover={this.setSelectedItem}
                            selectionKey={"code"}
                            iconOptions={{show: false}}
                            showValues={true}
                            order={"desc"}
                            onClick={this.onOptionClick}
                        />
                    </div>
                    <AutoSizer style={{width: "auto"}}>
                        {({width, height}) => <StackedBarChart
                            width={width - 40}
                            height={height * .9}
                            direction={"horizontal"}
                            showValuesLabels={true}
                            addTotalsItem={false}
                            data={data}
                            legendItems={legendItems}
                            margin={{
                                top: 10,
                                bottom: 10,
                                left: 30,
                                right: 10,
                            }}
                            texts={{}}
                            showAxesOptions={{
                                x: {line: false, tickLine: false},
                                y: {line: false, tickLine: false},
                            }}
                            order={"custom"}
                            legendOptions={{external: true}}
                            selectedItem={this.state.selectedItemKey}
                            onPathOver={this.setSelectedItem}
                            onClick={this.onBarClick}
                        />}
                    </AutoSizer>
                </VeBoxCard.Body>
            </VeBoxCard>
        );
        return (
            <div className="equipment-by-region-widget">
                {content}
            </div>
        );
    }

    public getText = function (guid: string): string {
        switch (guid) {
            case Placements.IN_UNKNOWN_LOCATION:
                return this.props.texts.equipmentInUnknownLocation;
            case Placements.ON_MARKET:
                return this.props.texts.equipmentOnMarket;
            case Placements.IN_WAREHOUSE:
                return this.props.texts.equipmentOnWarehouse;
            case Placements.AT_MANUFACTURERS:
                return this.props.texts.equipmentAtManufacturers;
            default:
                return "???";
        }
    };

    private onOptionClick = (key) => {
        (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
            sites: getSiteType(key),
            allTelemetries: this.props.withTelemetryDevice,
            applyFilter: true,
        });
    }

    private onBarClick = (data) => {
        const lev = this.state.equipmentByLevel.find((x) => x.levelName === data.x);
        if (!lev) {
            return;
        }
        if (lev.levelKey !== ZERO_GUID) {
            (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
                commercialHierarchy: {
                    hierarchyGuid: RSL_HIERARCHY_GUID,
                    nodes: [{
                        nodeGuid: lev.levelKey,
                    }],
                },
                sites: getSiteType(data.y),
                allTelemetries: this.props.withTelemetryDevice,
                applyFilter: true,
            });
        } else if (lev.levelKey === ZERO_GUID && this.state.equipmentByLevel.length === 1) {
            (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
                sites: getSiteType(data.y),
                allTelemetries: this.props.withTelemetryDevice,
                applyFilter: true,
            });
        }
    }
}
